/**
 * Initialize Base Data
 * Sets up default configurations for the nursing home costing system
 */

import { baseConfigService, BaseConfigurations } from './firestore';
import {
  PositionType,
  SalaryConfig,
  SocialChargeConfig,
  StaffRatioConfig,
  GeneralExpenseConfig,
  OperationalExpenses,
  ExpenseCategory,
  RoleDescriptionConfig,
} from '@/types';

/**
 * Default salary configurations for Costa Rica (in CRC - Costa Rican Colones)
 */
const DEFAULT_SALARIES: Record<PositionType, SalaryConfig> = {
  registered_nurse: {
    baseAmount: 650000, // CRC per month - morning shift
    afternoonShift: 650000, // CRC per month - afternoon shift
    nightShiftAmount: 715000, // 10% night differential
    currency: 'CRC',
  },
  head_nurse: {
    baseAmount: 750000,
    currency: 'CRC',
  },
  float_nurse: {
    baseAmount: 650000,
    nightShiftAmount: 682500, // 5% differential
    currency: 'CRC',
  },
  resident_assistant: {
    baseAmount: 450000, // Morning shift
    afternoonShift: 450000, // Afternoon shift
    nightShiftAmount: 495000, // 10% night differential
    currency: 'CRC',
  },
  bathroom_assistant: {
    baseAmount: 460000, // Morning shift
    afternoonShift: 460000, // Afternoon shift
    nightShiftAmount: 506000, // 10% night differential
    currency: 'CRC',
  },
  supernumerary: {
    baseAmount: 450000,
    currency: 'CRC',
  },
  extra_assistant: {
    baseAmount: 450000, // Morning shift
    afternoonShift: 450000, // Afternoon shift
    nightShiftAmount: 495000, // 10% night differential
    currency: 'CRC',
  },
  cook: {
    baseAmount: 500000,
    nightShiftAmount: 525000, // 5% night differential
    currency: 'CRC',
  },
  kitchen_assistant: {
    baseAmount: 380000,
    currency: 'CRC',
  },
  dishwasher: {
    baseAmount: 350000,
    currency: 'CRC',
  },
  kitchen_staff: {
    baseAmount: 420000,
    currency: 'CRC',
  },
  laundry: {
    baseAmount: 420000,
    currency: 'CRC',
  },
  laundry_staff: {
    baseAmount: 420000,
    currency: 'CRC',
  },
  doctor: {
    baseAmount: 1200000,
    currency: 'CRC',
  },
  physiotherapist: {
    baseAmount: 800000,
    currency: 'CRC',
  },
  administrador: {
    baseAmount: 900000, // Administrator salary
    currency: 'CRC',
  },
  asistente_administracion: {
    baseAmount: 550000, // Administrative assistant salary
    currency: 'CRC',
  },
};

/**
 * Default social charges for Costa Rica (as of 2024)
 */
const DEFAULT_SOCIAL_CHARGES: SocialChargeConfig = {
  ccss: 26.67, // CCSS percentage
  ins: 1.7, // INS percentage
  other: 2.63, // Other charges (vacation bonus, etc.)
};

/**
 * Default staff ratios based on industry standards
 */
const DEFAULT_STAFF_RATIOS: StaffRatioConfig = {
  nursing: {
    corte: 32,
    tope: 19,
    capacityRatio: 0,
  },
  generalAssistants: {
    corte: 20,
    tope: 3,
    capacityRatio: 20,
  },
  bathroomAssistants: {
    corte: 7,
    tope: 2,
    capacityRatio: 0,
  },
  extraAssistants: {
    corte: 20,
    tope: 0,
    capacityRatio: 0,
  },
  doctors: {
    corte: 50,
    tope: 0,
    capacityRatio: 0,
  },
  physiotherapy: {
    corte: 40,
    tope: 2,
    capacityRatio: 0,
  },
  kitchen: {
    cocinero: {
      corte: 300,
      tope: 0,
      capacityRatio: 0,
    },
    ayudante: {
      corte: 60,
      tope: 0,
      capacityRatio: 0,
    },
    pilero: {
      corte: 90,
      tope: 0,
      capacityRatio: 0,
    },
  },
  laundry: {
    corte: 50,
    tope: 10,
    capacityRatio: 0,
  },
  maintenance: {
    corte: 100,
    tope: 20,
    capacityRatio: 0,
  },
  administrative: {
    administrador: {
      corte: 80, // 1 administrator per 80 residents (minimum 1 per facility)
      tope: 1, // Minimum 1 administrator regardless of size
      capacityRatio: 0, // Not used in current formula
    },
    asistenteAdministracion: {
      corte: 60, // 1 administrative assistant per 60 residents
      tope: 1, // Minimum 1 assistant for facilities over 30 residents
      capacityRatio: 0, // Not used in current formula
    },
  },
  version: '1.0.0',
  lastUpdated: new Date(),
  updatedBy: 'system',
  description: 'Default staff ratio parameters for staffing calculations',
};

/**
 * Default general expenses
 */
const DEFAULT_GENERAL_EXPENSES: GeneralExpenseConfig = {
  fixedCosts: 5000000, // Fixed monthly costs in CRC
  variableCostPerResident: 150000, // Variable cost per resident per month in CRC
  currency: 'CRC',
};

/**
 * Helper function to create expense category with default CRC currency
 */
const createExpenseCategory = (
  amount: number,
  calculationMethod: ExpenseCategory['calculationMethod'] = 'fixed',
  baseValue?: number
): ExpenseCategory => ({
  amount,
  calculationMethod,
  baseValue,
  currency: 'CRC',
});

/**
 * Default operational expenses configuration for Costa Rican nursing homes
 * Based on industry standards and typical operational costs
 */
const DEFAULT_OPERATIONAL_EXPENSES: OperationalExpenses = {
  gastosFijos: {
    ambulancia: createExpenseCategory(45000, 'fixed'), // Monthly ambulance service fee
    combustible: createExpenseCategory(180000, 'fixed'), // Monthly fuel costs for transportation
    agua: createExpenseCategory(250000, 'fixed'), // Water utility bill
    cnfl: createExpenseCategory(320000, 'fixed'), // Electricity bill (CNFL)
    telecomunicaciones: createExpenseCategory(85000, 'fixed'), // Phone and internet services
    insumosLimpieza: createExpenseCategory(150000, 'fixed'), // Cleaning supplies
    gas: createExpenseCategory(120000, 'fixed'), // Gas for cooking and hot water
    mensajeria: createExpenseCategory(35000, 'fixed'), // Courier and messaging services
    contabilidad: createExpenseCategory(200000, 'fixed'), // Accounting services
    papeleriaInsumos: createExpenseCategory(75000, 'fixed'), // Office supplies and stationery
    cementerio: createExpenseCategory(25000, 'fixed'), // Cemetery and funeral-related costs
    licenciasDerechos: createExpenseCategory(50000, 'fixed'), // Licenses and legal fees
    comisionesBanco: createExpenseCategory(45000, 'fixed'), // Bank commissions and fees
    fumigacion: createExpenseCategory(80000, 'fixed'), // Pest control services
    mantenimientoLavanderia: createExpenseCategory(65000, 'fixed'), // Laundry equipment maintenance
    serviciosGenerales: createExpenseCategory(90000, 'fixed'), // General services (fire extinguishers, etc.)
  },
  panales: createExpenseCategory(40000, 'per_resident'), // Adult diapers per resident
  lavanderia: createExpenseCategory(7500, 'per_resident'), // Laundry costs per resident
  abarrotesInsumos: createExpenseCategory(95000, 'per_resident'), // Food and supplies per resident
  otrosGastosMensuales: createExpenseCategory(1193625, 'fixed'), // Other monthly expenses as specified
  version: '1.0.0',
  lastUpdated: new Date(),
  updatedBy: 'system',
  description: 'Default operational expenses for Costa Rican nursing homes',
};

/**
 * Default role descriptions for all positions in Spanish (Costa Rica)
 */
const DEFAULT_ROLE_DESCRIPTIONS: Record<PositionType, RoleDescriptionConfig> = {
  registered_nurse: {
    name: 'Enfermero(a) Registrado(a)',
    description:
      'Profesional de enfermería responsable del cuidado directo de residentes, administración de medicamentos y supervisión del personal auxiliar.',
    responsibilities: [
      'Administración de medicamentos según prescripción médica',
      'Supervisión y coordinación del personal auxiliar de enfermería',
      'Evaluación y monitoreo del estado de salud de los residentes',
      'Elaboración y actualización de planes de cuidado',
      'Comunicación con familiares y equipo médico',
      'Manejo de emergencias médicas',
      'Documentación de cuidados en expedientes clínicos',
    ],
    requirements:
      'Licenciatura en Enfermería y colegiatura vigente en el Colegio de Enfermeros de Costa Rica',
    category: 'nursing',
  },
  head_nurse: {
    name: 'Jefe(a) de Enfermería',
    description:
      'Profesional de enfermería con funciones administrativas y de supervisión general del departamento de enfermería.',
    responsibilities: [
      'Supervisión general del departamento de enfermería',
      'Coordinación de horarios y turnos del personal',
      'Evaluación del desempeño del personal de enfermería',
      'Desarrollo de protocolos y procedimientos',
      'Gestión de recursos y suministros médicos',
      'Capacitación y educación continua del personal',
      'Reporte a la administración general',
    ],
    requirements:
      'Licenciatura en Enfermería, experiencia en supervisión y liderazgo',
    category: 'nursing',
  },
  float_nurse: {
    name: 'Enfermero(a) Días Libres',
    description:
      'Enfermero de reemplazo para cubrir días libres, vacaciones y ausencias del personal titular.',
    responsibilities: [
      'Cobertura de turnos por ausencias del personal titular',
      'Administración de medicamentos en turnos asignados',
      'Cuidado directo de residentes según protocolos',
      'Apoyo en emergencias médicas',
      'Documentación de actividades realizadas',
      'Comunicación efectiva con el equipo de relevo',
    ],
    requirements:
      'Licenciatura en Enfermería y disponibilidad para horarios variables',
    category: 'professionals',
  },
  resident_assistant: {
    name: 'Asistente de Pacientes',
    description:
      'Personal auxiliar responsable del cuidado personal y asistencia en actividades de la vida diaria de los residentes.',
    responsibilities: [
      'Asistencia en higiene personal y vestimenta',
      'Apoyo en alimentación y hidratación',
      'Movilización y traslado de residentes',
      'Acompañamiento en actividades recreativas',
      'Monitoreo básico de signos vitales',
      'Comunicación de cambios en el estado de residentes',
      'Mantenimiento del orden en habitaciones',
    ],
    requirements:
      'Educación secundaria completa, curso de asistente de pacientes preferible',
    category: 'assistants',
  },
  bathroom_assistant: {
    name: 'Asistente de Baños',
    description:
      'Personal especializado en asistencia de higiene personal y cuidados relacionados con el baño de residentes.',
    responsibilities: [
      'Asistencia especializada en higiene personal',
      'Manejo de residentes con incontinencia',
      'Cambio de pañales y productos de higiene',
      'Limpieza y desinfección de áreas de baño',
      'Apoyo en movilización para actividades de aseo',
      'Mantenimiento de inventario de productos de higiene',
      'Reporte de condiciones de salud observadas',
    ],
    requirements:
      'Educación secundaria, experiencia en cuidado de adultos mayores',
    category: 'assistants',
  },
  supernumerary: {
    name: 'Asistente Supernumerario',
    description:
      'Personal auxiliar de apoyo general para cubrir necesidades adicionales y días libres del personal regular.',
    responsibilities: [
      'Apoyo general en cuidado de residentes',
      'Cobertura por ausencias del personal titular',
      'Asistencia en actividades grupales',
      'Apoyo en alimentación y movilización',
      'Limpieza básica de áreas comunes',
      'Acompañamiento a residentes',
    ],
    requirements: 'Educación secundaria, experiencia en cuidado de personas',
    category: 'assistants',
  },
  extra_assistant: {
    name: 'Asistente Extra',
    description:
      'Personal auxiliar adicional para refuerzo en períodos de alta demanda o necesidades especiales.',
    responsibilities: [
      'Apoyo adicional en cuidado de residentes',
      'Asistencia en actividades especiales',
      'Refuerzo en turnos con alta ocupación',
      'Apoyo en emergencias o situaciones especiales',
      'Colaboración con personal regular',
    ],
    requirements:
      'Educación secundaria, disponibilidad para horarios flexibles',
    category: 'assistants',
  },
  cook: {
    name: 'Cocinero(a)',
    description:
      'Profesional responsable de la preparación de alimentos según dietas especializadas para adultos mayores.',
    responsibilities: [
      'Preparación de menús según dietas médicas especializadas',
      'Supervisión de la calidad e inocuidad de alimentos',
      'Coordinación del personal de cocina',
      'Control de inventarios y suministros',
      'Cumplimiento de normas de higiene alimentaria',
      'Adaptación de alimentos para diferentes necesidades',
      'Planificación de menús semanales',
    ],
    requirements:
      'Formación en gastronomía o cocina, experiencia en cocina institucional',
    category: 'services',
  },
  kitchen_assistant: {
    name: 'Asistente de Cocina',
    description:
      'Personal de apoyo en la preparación y distribución de alimentos en el área de cocina.',
    responsibilities: [
      'Apoyo en preparación de alimentos',
      'Distribución de comidas a residentes',
      'Limpieza y organización del área de cocina',
      'Lavado de utensilios y equipos',
      'Control básico de temperaturas de alimentos',
      'Apoyo en alimentación de residentes',
      'Mantenimiento de higiene en cocina',
    ],
    requirements:
      'Educación primaria completa, curso de manipulación de alimentos',
    category: 'services',
  },
  dishwasher: {
    name: 'Pilero(a)',
    description:
      'Personal responsable del lavado, desinfección y mantenimiento de utensilios de cocina y comedor.',
    responsibilities: [
      'Lavado y desinfección de utensilios y vajilla',
      'Limpieza profunda de equipos de cocina',
      'Organización y almacenamiento de utensilios limpios',
      'Mantenimiento de áreas de lavado',
      'Control de inventario de productos de limpieza',
      'Apoyo en limpieza general de cocina',
    ],
    requirements:
      'Educación primaria, experiencia en lavado industrial preferible',
    category: 'services',
  },
  kitchen_staff: {
    name: 'Personal de Cocina',
    description:
      'Personal general del área de cocina que incluye cocineros, asistentes y personal de limpieza.',
    responsibilities: [
      'Funciones combinadas de preparación y limpieza',
      'Apoyo integral en operaciones de cocina',
      'Distribución de alimentos',
      'Mantenimiento general del área',
    ],
    requirements: 'Educación básica, experiencia en servicios de alimentación',
    category: 'services',
  },
  laundry: {
    name: 'Personal de Lavandería',
    description:
      'Responsable del lavado, secado y mantenimiento de ropa de cama, vestimenta y textiles del hogar.',
    responsibilities: [
      'Lavado y desinfección de ropa de residentes',
      'Manejo de ropa de cama y textiles institucionales',
      'Clasificación y tratamiento de ropa contaminada',
      'Operación de equipos de lavandería',
      'Control de inventario de productos de lavado',
      'Reparaciones menores de textiles',
      'Distribución de ropa limpia',
    ],
    requirements: 'Educación primaria, experiencia en lavandería institucional',
    category: 'services',
  },
  laundry_staff: {
    name: 'Asistente de Lavandería',
    description:
      'Personal de apoyo en las operaciones de lavandería y mantenimiento de textiles.',
    responsibilities: [
      'Apoyo en operaciones de lavado y secado',
      'Clasificación y organización de ropa',
      'Limpieza y mantenimiento del área',
      'Distribución de ropa limpia',
    ],
    requirements: 'Educación básica, disponibilidad para trabajo físico',
    category: 'services',
  },
  doctor: {
    name: 'Médico',
    description:
      'Profesional médico responsable de la atención médica integral y supervisión de la salud de los residentes.',
    responsibilities: [
      'Evaluación médica integral de residentes',
      'Diagnóstico y tratamiento de condiciones médicas',
      'Prescripción de medicamentos y tratamientos',
      'Coordinación con especialistas externos',
      'Supervisión de cuidados médicos',
      'Atención de emergencias médicas',
      'Comunicación con familiares sobre estado de salud',
    ],
    requirements:
      'Título de médico cirujano y colegiatura vigente en el Colegio de Médicos de Costa Rica',
    category: 'professionals',
  },
  physiotherapist: {
    name: 'Fisioterapeuta',
    description:
      'Profesional especializado en rehabilitación física y mantenimiento de la movilidad de residentes.',
    responsibilities: [
      'Evaluación de capacidades físicas y motoras',
      'Diseño de programas de rehabilitación',
      'Aplicación de terapias físicas especializadas',
      'Entrenamiento en uso de ayudas técnicas',
      'Prevención de complicaciones por inmovilidad',
      'Educación a familiares sobre ejercicios',
      'Coordinación con equipo médico multidisciplinario',
    ],
    requirements: 'Licenciatura en Fisioterapia y colegiatura vigente',
    category: 'professionals',
  },
  administrador: {
    name: 'Administrador(a)',
    description:
      'Profesional responsable de la gestión administrativa general y coordinación operativa del hogar de adulto mayor.',
    responsibilities: [
      'Gestión administrativa general del hogar',
      'Supervisión de personal operativo',
      'Coordinación con familiares y autoridades',
      'Gestión de recursos humanos y materiales',
      'Cumplimiento de regulaciones y estándares',
      'Planificación y control presupuestario',
      'Reporte a propietarios o junta directiva',
    ],
    requirements:
      'Licenciatura en Administración o carrera afín, experiencia en gestión de servicios de salud',
    category: 'administrative',
  },
  asistente_administracion: {
    name: 'Asistente de Administración',
    description:
      'Personal de apoyo administrativo para gestión documental, comunicaciones y tareas administrativas generales.',
    responsibilities: [
      'Apoyo en tareas administrativas generales',
      'Gestión documental y archivo',
      'Atención telefónica y comunicaciones',
      'Apoyo en gestión de recursos humanos',
      'Manejo de correspondencia y documentos',
      'Apoyo en coordinación con proveedores',
      'Asistencia en reportes y controles',
    ],
    requirements:
      'Bachillerato en educación media, conocimientos de oficina y computación básica',
    category: 'administrative',
  },
};

/**
 * Initialize base configurations if they don't exist
 */
export const initializeBaseConfigurations = async (
  updatedBy: string
): Promise<void> => {
  try {
    const { logger } = await import('@/utils/logger');
    logger.debug('Checking for existing base configurations');

    // Check if base configurations already exist
    const existingConfig = await baseConfigService.getBaseConfigurations();

    logger.debug('Existing config check result', {
      hasExistingConfig: !!existingConfig,
      existingConfigKeys: existingConfig ? Object.keys(existingConfig) : [],
    });

    if (!existingConfig) {
      logger.info('Initializing base configurations');

      const baseConfig: Omit<BaseConfigurations, 'lastUpdated'> = {
        salaries: DEFAULT_SALARIES,
        socialCharges: DEFAULT_SOCIAL_CHARGES,
        staffRatios: DEFAULT_STAFF_RATIOS,
        generalExpenses: DEFAULT_GENERAL_EXPENSES,
        operationalExpenses: DEFAULT_OPERATIONAL_EXPENSES,
        roleDescriptions: DEFAULT_ROLE_DESCRIPTIONS,
        updatedBy,
      };

      await baseConfigService.updateBaseConfigurations(baseConfig, updatedBy);
      logger.info('Base configurations initialized successfully');
    } else {
      logger.info('Base configurations already exist');
    }
  } catch (error) {
    const { logger } = await import('@/utils/logger');
    logger.error('Error initializing base configurations', error);
    throw error;
  }
};

/**
 * Update base configurations with new values
 */
export const updateBaseConfigurationsFromDefaults = async (
  updatedBy: string
): Promise<void> => {
  try {
    const { logger } = await import('@/utils/logger');
    logger.info('Updating base configurations from defaults');

    const baseConfig: Omit<BaseConfigurations, 'lastUpdated'> = {
      salaries: DEFAULT_SALARIES,
      socialCharges: DEFAULT_SOCIAL_CHARGES,
      staffRatios: DEFAULT_STAFF_RATIOS,
      generalExpenses: DEFAULT_GENERAL_EXPENSES,
      operationalExpenses: DEFAULT_OPERATIONAL_EXPENSES,
      roleDescriptions: DEFAULT_ROLE_DESCRIPTIONS,
      updatedBy,
    };

    await baseConfigService.updateBaseConfigurations(baseConfig, updatedBy);
    logger.info('Base configurations updated successfully');
  } catch (error) {
    const { logger } = await import('@/utils/logger');
    logger.error('Error updating base configurations', error);
    throw error;
  }
};

/**
 * Get the current Costa Rican social charge rates
 */
export const getCurrentSocialChargeRates = (): SocialChargeConfig => {
  return { ...DEFAULT_SOCIAL_CHARGES };
};

/**
 * Calculate total social charge percentage
 */
export const getTotalSocialChargePercentage = (
  charges: SocialChargeConfig = DEFAULT_SOCIAL_CHARGES
): number => {
  return charges.ccss + charges.ins + charges.other;
};

/**
 * Validate salary configuration
 */
export const validateSalaryConfig = (config: SalaryConfig): boolean => {
  return (
    config.baseAmount > 0 &&
    config.currency === 'CRC' &&
    (config.nightShiftAmount === undefined ||
      config.nightShiftAmount >= config.baseAmount)
  );
};

/**
 * Validate social charge configuration
 */
export const validateSocialChargeConfig = (
  config: SocialChargeConfig
): boolean => {
  return (
    config.ccss >= 0 &&
    config.ccss <= 100 &&
    config.ins >= 0 &&
    config.ins <= 100 &&
    config.other >= 0 &&
    config.other <= 100
  );
};

/**
 * Validate expense category configuration
 */
export const validateExpenseCategory = (category: ExpenseCategory): boolean => {
  return (
    category.amount >= 0 &&
    category.currency === 'CRC' &&
    ['per_resident', 'fixed', 'percentage'].includes(category.calculationMethod) &&
    (category.calculationMethod !== 'percentage' || 
     (category.baseValue !== undefined && category.baseValue > 0))
  );
};

/**
 * Validate operational expenses configuration
 */
export const validateOperationalExpenses = (
  config: OperationalExpenses
): boolean => {
  // Validate all GASTOS FIJOS subcategories
  const gastosFijosValid = Object.values(config.gastosFijos).every(
    validateExpenseCategory
  );

  // Validate other expense categories
  const otherCategoriesValid = [
    config.panales,
    config.lavanderia,
    config.abarrotesInsumos,
    config.otrosGastosMensuales,
  ].every(validateExpenseCategory);

  return gastosFijosValid && otherCategoriesValid && config.version.length > 0;
};

/**
 * Get default operational expenses configuration
 */
export const getDefaultOperationalExpenses = (): OperationalExpenses => {
  return JSON.parse(JSON.stringify(DEFAULT_OPERATIONAL_EXPENSES));
};
